import { useState } from "react";
import {
  Briefcase,
  User,
  Building2,
  Calendar,
  Plus,
  Sliders,
  ChevronDown,
  ChevronUp,
  Info,
  Type,
  AlignLeft,
  List,
  Hash,
  DollarSign,
  CalendarDays,
  Mail,
  Phone,
  Check,
  Edit2,
  Trash2,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  useEntityCustomFields,
  ENTITY_FIELD_TYPE_LABELS,
  type EntityFieldType,
} from "../misc/useEntityCustomFields";
import {
  AddEntityFieldDialog,
  EditEntityFieldsDialog,
  ReorderEntityFieldsDialog,
} from "../misc/EntityCustomFieldDialogs";
import {
  useDealCustomFields,
  CUSTOM_FIELD_TYPE_LABELS,
} from "../deals/useDealCustomFields";
import {
  AddCustomFieldDialog,
  EditCustomFieldsDialog,
  ReorderCustomFieldsDialog,
} from "../deals/DealCustomFieldDialogs";

type TabType = "deal" | "contact" | "company" | "task";

interface Suggestion {
  name: string;
  type: EntityFieldType;
  description: string;
  options?: string[];
}

const SUGGESTIONS: Record<TabType, Suggestion[]> = {
  deal: [
    {
      name: "Origem do Lead",
      type: "select",
      options: [
        "Site / Formulário",
        "WhatsApp",
        "Indicação",
        "Tráfego Pago",
        "Evento",
        "Outro",
      ],
      description: "Selecione a origem ou canal de aquisição deste negócio",
    },
    {
      name: "Canal de Entrada",
      type: "select",
      options: ["Inbound", "Outbound", "Parceiro", "Direto"],
      description: "Canal por onde o lead chegou ao time comercial",
    },
    {
      name: "Motivo de Perda Detalhado",
      type: "text",
      description: "Anotação livre do motivo de cancelamento ou perda",
    },
  ],
  contact: [
    {
      name: "Endereço Completo",
      type: "text",
      description: "Rua, número, complemento, bairro e cidade da pessoa",
    },
    {
      name: "Origem / Fonte",
      type: "select",
      options: ["LinkedIn", "Indicação", "Evento", "Prospecção Ativa", "Outro"],
      description: "Selecione a origem da pessoa em uma lista personalizável",
    },
    {
      name: "Data de Nascimento",
      type: "date",
      description: "Data de aniversário para relacionamentos e felicitações",
    },
  ],
  company: [
    {
      name: "Endereço Completo",
      type: "text",
      description: "Logradouro, número, bairro, cidade e CEP da empresa",
    },
    {
      name: "Inscrição Estadual",
      type: "text",
      description: "Número de registro da Inscrição Estadual (IE)",
    },
    {
      name: "Origem da Empresa",
      type: "select",
      options: [
        "Prospecção Ativa",
        "Inbound",
        "Indicação de Cliente",
        "Parceria",
        "Outro",
      ],
      description: "Canal pelo qual a organização foi captada",
    },
  ],
  task: [
    {
      name: "Local / Link da Reunião",
      type: "text",
      description: "Endereço presencial ou link da chamada de vídeo",
    },
    {
      name: "Prioridade",
      type: "select",
      options: ["Baixa", "Média", "Alta", "Urgente"],
      description: "Nível de urgência da atividade a ser realizada",
    },
  ],
};

const DEFAULT_DEAL_FIELDS = [
  { id: "sys_deal_name", name: "Título do Negócio", type: "text" },
  { id: "sys_deal_value", name: "Valor do Negócio", type: "currency" },
  { id: "sys_deal_pipeline", name: "Funil de Vendas", type: "select" },
  { id: "sys_deal_stage", name: "Etapa do Funil", type: "select" },
  {
    id: "sys_deal_expected_closing",
    name: "Previsão de Fechamento",
    type: "date",
  },
  { id: "sys_deal_owner", name: "Responsável", type: "user" },
  { id: "sys_deal_org", name: "Organização", type: "organization" },
  { id: "sys_deal_contact", name: "Pessoa de Contato", type: "contact" },
];

const DEFAULT_TASK_FIELDS = [
  { id: "sys_task_title", name: "Título da Atividade", type: "text" },
  { id: "sys_task_type", name: "Tipo da Atividade", type: "select" },
  { id: "sys_task_due_date", name: "Data de Vencimento", type: "date" },
  { id: "sys_task_due_time", name: "Horário", type: "time" },
  { id: "sys_task_assignee", name: "Responsável", type: "user" },
  { id: "sys_task_notes", name: "Observações / Descrição", type: "textarea" },
];

export const DataFieldsSettings = () => {
  const [activeTab, setActiveTab] = useState<TabType>("contact");
  const [customFieldsExpanded, setCustomFieldsExpanded] = useState(true);
  const [standardFieldsExpanded, setStandardFieldsExpanded] = useState(true);

  // Contact & Company hooks
  const contactCustom = useEntityCustomFields("contact");
  const companyCustom = useEntityCustomFields("company");

  // Deal hook
  const dealCustom = useEntityCustomFieldsHookOrDeal();

  // Dialogs state
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openReorderDialog, setOpenReorderDialog] = useState(false);

  // Field type icon helper
  const getFieldIcon = (type: string) => {
    switch (type) {
      case "text":
        return <Type className="w-3.5 h-3.5 text-muted-foreground" />;
      case "textarea":
        return <AlignLeft className="w-3.5 h-3.5 text-muted-foreground" />;
      case "select":
      case "multiselect":
      case "autocomplete":
        return <List className="w-3.5 h-3.5 text-muted-foreground" />;
      case "number":
        return <Hash className="w-3.5 h-3.5 text-muted-foreground" />;
      case "currency":
        return (
          <DollarSign className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
        );
      case "date":
        return <CalendarDays className="w-3.5 h-3.5 text-muted-foreground" />;
      case "email":
        return <Mail className="w-3.5 h-3.5 text-muted-foreground" />;
      case "tel":
        return <Phone className="w-3.5 h-3.5 text-muted-foreground" />;
      case "time":
        return <Clock className="w-3.5 h-3.5 text-muted-foreground" />;
      case "user":
        return <User className="w-3.5 h-3.5 text-muted-foreground" />;
      case "organization":
        return <Building2 className="w-3.5 h-3.5 text-muted-foreground" />;
      case "contact":
        return <User className="w-3.5 h-3.5 text-muted-foreground" />;
      default:
        return <Type className="w-3.5 h-3.5 text-muted-foreground" />;
    }
  };

  const getActiveTabLabel = () => {
    switch (activeTab) {
      case "deal":
        return "Lead / Negócio";
      case "contact":
        return "Pessoa";
      case "company":
        return "Organização";
      case "task":
        return "Atividade";
    }
  };

  const currentHook =
    activeTab === "contact"
      ? contactCustom
      : activeTab === "company"
        ? companyCustom
        : null;

  // Custom fields for active tab
  const activeCustomFields =
    activeTab === "contact" || activeTab === "company"
      ? (currentHook?.allFields || []).filter((f) => !f.id.startsWith("sys_"))
      : activeTab === "deal"
        ? dealCustom.fields
        : [];

  // Standard fields for active tab
  const activeStandardFields =
    activeTab === "contact" || activeTab === "company"
      ? (currentHook?.allFields || []).filter((f) => f.id.startsWith("sys_"))
      : activeTab === "deal"
        ? DEFAULT_DEAL_FIELDS
        : DEFAULT_TASK_FIELDS;

  const handleApplySuggestion = (sug: Suggestion) => {
    if (activeTab === "contact" || activeTab === "company") {
      currentHook?.addField({
        name: sug.name,
        type: sug.type,
        options: sug.options,
        visibility: {
          showOnDealCreate: true,
          showOnEntityCreate: true,
          showOnDealPanel: true,
        },
      });
    } else if (activeTab === "deal") {
      dealCustom.addField({
        name: sug.name,
        type: sug.type as any,
        options: sug.options,
        showOnCreate: true,
      });
    }
  };

  return (
    <Card
      id="data-fields"
      className="overflow-hidden border border-border/70 shadow-sm"
    >
      <CardContent className="p-0">
        {/* Top Header */}
        <div className="p-6 pb-4 border-b border-border/60 bg-card">
          <div className="space-y-1">
            <h2 className="text-xl font-bold tracking-tight text-foreground">
              Campos de Dados
            </h2>
            <p className="text-xs text-muted-foreground">
              Configure campos padrão e campos personalizados para enriquecer
              seus cadastros e negócios.
            </p>
          </div>

          {/* Navigation Tabs (Lead/negócio, Pessoa, Organização, Atividade - sem produtos) */}
          <div className="flex items-center gap-6 mt-6 border-b border-border/60 -mb-4">
            <button
              type="button"
              onClick={() => setActiveTab("deal")}
              className={cn(
                "flex items-center gap-2 pb-3 text-sm font-medium transition-colors border-b-2 cursor-pointer",
                activeTab === "deal"
                  ? "border-primary text-primary font-semibold"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              <Briefcase className="w-4 h-4" />
              Lead/negócio
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("contact")}
              className={cn(
                "flex items-center gap-2 pb-3 text-sm font-medium transition-colors border-b-2 cursor-pointer",
                activeTab === "contact"
                  ? "border-primary text-primary font-semibold"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              <User className="w-4 h-4" />
              Pessoa
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("company")}
              className={cn(
                "flex items-center gap-2 pb-3 text-sm font-medium transition-colors border-b-2 cursor-pointer",
                activeTab === "company"
                  ? "border-primary text-primary font-semibold"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              <Building2 className="w-4 h-4" />
              Organização
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("task")}
              className={cn(
                "flex items-center gap-2 pb-3 text-sm font-medium transition-colors border-b-2 cursor-pointer",
                activeTab === "task"
                  ? "border-primary text-primary font-semibold"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              <Calendar className="w-4 h-4" />
              Atividade
            </button>
          </div>
        </div>

        {/* Action Bar */}
        <div className="px-6 py-4 bg-muted/20 border-b border-border/50 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              onClick={() => setOpenAddDialog(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium gap-1.5 h-8 text-xs cursor-pointer shadow-xs"
            >
              <Plus className="w-4 h-4" />
              Campo personalizado
            </Button>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setOpenReorderDialog(true)}
              className="h-8 text-xs font-medium gap-1.5 cursor-pointer"
            >
              <Sliders className="w-3.5 h-3.5" />
              Organizar campos
            </Button>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer transition-colors"
                  >
                    <Info className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs text-xs">
                  Defina quais campos são visíveis na criação rápida, nos cards
                  de negócios e nos detalhes de{" "}
                  {getActiveTabLabel().toLowerCase()}.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setOpenEditDialog(true)}
            className="text-xs text-primary hover:text-primary/80 gap-1 h-8 cursor-pointer"
          >
            <Edit2 className="w-3.5 h-3.5" />
            Configurar Visibilidade de Todos
          </Button>
        </div>

        {/* Section 1: Campos Personalizados */}
        <div className="border-b border-border/60">
          <button
            type="button"
            onClick={() => setCustomFieldsExpanded((v) => !v)}
            className="w-full flex items-center justify-between px-6 py-3 bg-muted/30 hover:bg-muted/50 transition-colors text-left cursor-pointer"
          >
            <div className="flex items-center gap-2">
              {customFieldsExpanded ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
              <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
                Campos personalizados ({activeCustomFields.length})
              </span>
            </div>
          </button>

          {customFieldsExpanded && (
            <div className="p-6">
              {activeCustomFields.length === 0 ? (
                /* Empty state matching user's reference image */
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 py-4">
                  {/* Illustration badge */}
                  <div className="w-24 h-24 rounded-full bg-blue-500/10 border border-blue-500/20 flex flex-col items-center justify-center p-2 text-primary shadow-xs shrink-0 select-none">
                    <div className="font-mono text-xs font-bold border border-blue-400/40 px-1.5 py-0.5 rounded bg-card text-foreground">
                      abc|
                    </div>
                    <div className="flex gap-1 mt-1 text-[10px]">
                      <span className="px-1 rounded bg-blue-500/20 font-bold">
                        !
                      </span>
                      <span className="px-1 rounded bg-blue-500/20 font-bold">
                        !!
                      </span>
                    </div>
                    <div className="text-[10px] font-mono mt-0.5 font-semibold text-muted-foreground">
                      123
                    </div>
                  </div>

                  {/* Copy & suggestions */}
                  <div className="space-y-3 text-center sm:text-left">
                    <div>
                      <h3 className="text-base font-semibold text-foreground">
                        Nenhum campo personalizado de{" "}
                        {getActiveTabLabel().toLowerCase()} ainda
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Você pode criar um campo do zero ou a partir das nossas
                        sugestões de campos populares:
                      </p>
                    </div>

                    <div className="space-y-2">
                      {SUGGESTIONS[activeTab]?.map((sug, idx) => (
                        <div
                          key={idx}
                          className="flex flex-col sm:flex-row sm:items-center gap-2 text-xs"
                        >
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleApplySuggestion(sug)}
                            className="h-7 text-xs font-medium gap-1 self-start cursor-pointer hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
                          >
                            <Plus className="w-3 h-3" />
                            {sug.name}
                          </Button>
                          <span className="text-muted-foreground text-[11px]">
                            {sug.description}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                /* Table with custom fields */
                <div className="divide-y divide-border/50 border border-border/60 rounded-lg overflow-hidden bg-card">
                  {activeCustomFields.map((f: any) => {
                    const typeLabel =
                      (ENTITY_FIELD_TYPE_LABELS as Record<string, string>)[
                        f.type
                      ] ||
                      (CUSTOM_FIELD_TYPE_LABELS as Record<string, string>)[
                        f.type
                      ] ||
                      f.type;

                    const vis = f.visibility as
                      | {
                          showOnDealCreate: boolean;
                          showOnEntityCreate: boolean;
                          showOnDealPanel: boolean;
                        }
                      | undefined;

                    return (
                      <div
                        key={f.id}
                        className="flex items-center justify-between p-3 px-4 hover:bg-muted/40 transition-colors group"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-7 h-7 rounded border border-border/60 bg-muted/30 flex items-center justify-center shrink-0">
                            {getFieldIcon(f.type)}
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-xs font-semibold text-foreground truncate">
                              {f.name}
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              {typeLabel}
                            </span>
                          </div>
                        </div>

                        {/* Visibility badges & actions */}
                        <div className="flex items-center gap-3">
                          {vis ? (
                            <div className="hidden sm:flex items-center gap-1.5">
                              {vis.showOnDealCreate && (
                                <Badge
                                  variant="secondary"
                                  className="text-[10px] bg-blue-500/10 text-blue-600 dark:text-blue-400 border-0"
                                >
                                  + Deal
                                </Badge>
                              )}
                              {vis.showOnEntityCreate && (
                                <Badge
                                  variant="secondary"
                                  className="text-[10px] bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-0"
                                >
                                  + {getActiveTabLabel()}
                                </Badge>
                              )}
                              {vis.showOnDealPanel && (
                                <Badge
                                  variant="secondary"
                                  className="text-[10px] bg-purple-500/10 text-purple-600 dark:text-purple-400 border-0"
                                >
                                  Card Deal
                                </Badge>
                              )}
                            </div>
                          ) : f.showOnCreate ? (
                            <Badge
                              variant="secondary"
                              className="text-[10px] bg-blue-500/10 text-blue-600 dark:text-blue-400 border-0"
                            >
                              Na criação
                            </Badge>
                          ) : null}

                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setOpenEditDialog(true)}
                              className="h-7 text-xs px-2 text-muted-foreground hover:text-foreground cursor-pointer"
                              title="Editar campo"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (
                                  activeTab === "contact" ||
                                  activeTab === "company"
                                ) {
                                  currentHook?.deleteField(f.id);
                                } else if (activeTab === "deal") {
                                  dealCustom.deleteField(f.id);
                                }
                              }}
                              className="h-7 text-xs px-2 text-muted-foreground hover:text-destructive cursor-pointer"
                              title="Excluir campo"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Section 2: Campos Padrão do Sistema */}
        <div>
          <button
            type="button"
            onClick={() => setStandardFieldsExpanded((v) => !v)}
            className="w-full flex items-center justify-between px-6 py-3 bg-muted/30 hover:bg-muted/50 transition-colors text-left cursor-pointer"
          >
            <div className="flex items-center gap-2">
              {standardFieldsExpanded ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
              <div className="w-4 h-4 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-[10px]">
                P
              </div>
              <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
                Campos padrão ({activeStandardFields.length})
              </span>
            </div>
          </button>

          {standardFieldsExpanded && (
            <div className="p-6 pt-3">
              <div className="divide-y divide-border/50 border border-border/60 rounded-lg overflow-hidden bg-card">
                {/* Header row */}
                <div className="flex items-center justify-between px-4 py-2 bg-muted/40 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                  <span>Nome do campo</span>
                  <span className="hidden sm:inline">
                    Visibilidade / Status
                  </span>
                </div>

                {activeStandardFields.map((f: any) => {
                  const typeLabel =
                    (ENTITY_FIELD_TYPE_LABELS as Record<string, string>)[
                      f.type
                    ] ||
                    (CUSTOM_FIELD_TYPE_LABELS as Record<string, string>)[
                      f.type
                    ] ||
                    f.type;

                  const vis = f.visibility as
                    | {
                        showOnDealCreate: boolean;
                        showOnEntityCreate: boolean;
                        showOnDealPanel: boolean;
                      }
                    | undefined;

                  return (
                    <div
                      key={f.id}
                      className="flex items-center justify-between p-3 px-4 hover:bg-muted/40 transition-colors group"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-7 h-7 rounded border border-border/60 bg-muted/30 flex items-center justify-center shrink-0">
                          {getFieldIcon(f.type)}
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="text-xs font-semibold text-foreground truncate">
                            {f.name}
                          </span>
                          <span className="text-[11px] text-muted-foreground">
                            {typeLabel}
                          </span>
                        </div>
                      </div>

                      {/* Visibility indicators & Edit button */}
                      <div className="flex items-center gap-3">
                        {vis ? (
                          <div className="hidden sm:flex items-center gap-1.5">
                            {vis.showOnDealCreate && (
                              <Badge
                                variant="secondary"
                                className="text-[10px] bg-blue-500/10 text-blue-600 dark:text-blue-400 border-0"
                              >
                                + Deal
                              </Badge>
                            )}
                            {vis.showOnEntityCreate && (
                              <Badge
                                variant="secondary"
                                className="text-[10px] bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-0"
                              >
                                + {getActiveTabLabel()}
                              </Badge>
                            )}
                            {vis.showOnDealPanel && (
                              <Badge
                                variant="secondary"
                                className="text-[10px] bg-purple-500/10 text-purple-600 dark:text-purple-400 border-0"
                              >
                                Card Deal
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <div
                            className="text-emerald-600 dark:text-emerald-400"
                            title="Ativo no sistema"
                          >
                            <Check className="w-4 h-4" />
                          </div>
                        )}

                        {(activeTab === "contact" ||
                          activeTab === "company") && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setOpenEditDialog(true)}
                            className="h-7 text-xs px-2 text-muted-foreground hover:text-foreground cursor-pointer"
                            title="Editar regras deste campo"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </CardContent>

      {/* Dialogs for Contact & Company */}
      {(activeTab === "contact" || activeTab === "company") && (
        <>
          <AddEntityFieldDialog
            entity={activeTab}
            open={openAddDialog}
            onOpenChange={setOpenAddDialog}
          />
          <EditEntityFieldsDialog
            entity={activeTab}
            open={openEditDialog}
            onOpenChange={setOpenEditDialog}
          />
          <ReorderEntityFieldsDialog
            entity={activeTab}
            open={openReorderDialog}
            onOpenChange={setOpenReorderDialog}
          />
        </>
      )}

      {/* Dialogs for Deal */}
      {activeTab === "deal" && (
        <>
          <AddCustomFieldDialog
            open={openAddDialog}
            onOpenChange={setOpenAddDialog}
          />
          <EditCustomFieldsDialog
            open={openEditDialog}
            onOpenChange={setOpenEditDialog}
          />
          <ReorderCustomFieldsDialog
            open={openReorderDialog}
            onOpenChange={setOpenReorderDialog}
          />
        </>
      )}
    </Card>
  );
};

/** Deal custom fields bridge */
function useEntityCustomFieldsHookOrDeal() {
  const { fields, addField, updateField, deleteField, reorderFields } =
    useDealCustomFields();
  return { fields, addField, updateField, deleteField, reorderFields };
}
